import { useEffect } from 'react';
import { useLang } from './lang';
import { pageMeta } from './meta';
import type { Route } from './routes';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string, hreflang?: string) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(sel);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

/** Keeps the head in step with the language; the prerender writes one head per route and language. */
export function Seo({ route }: { route: Route }) {
  const { lang } = useLang();

  useEffect(() => {
    const m = pageMeta(route, lang);
    document.title = m.title;
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';

    setMeta('name', 'description', m.description);
    setMeta('property', 'og:title', m.ogTitle);
    setMeta('property', 'og:description', m.ogDescription);
    setMeta('property', 'og:url', m.url);
    setMeta('property', 'og:image', m.ogImage);
    setMeta('property', 'og:image:alt', m.ogImageAlt);
    setMeta('property', 'og:locale', lang === 'ar' ? 'ar' : 'en_GB');
    setMeta('property', 'og:locale:alternate', lang === 'ar' ? 'en_GB' : 'ar');
    setMeta('name', 'twitter:title', m.ogTitle);
    setMeta('name', 'twitter:description', m.ogDescription);
    setMeta('name', 'twitter:image', m.ogImage);

    setLink('canonical', m.url);
    for (const a of m.alternates) setLink('alternate', a.href, a.hreflang);

    let script = document.getElementById('iris-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'iris-jsonld';
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(m.jsonld);
  }, [route, lang]);

  return null;
}
